import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import type { ProvisionedSite } from "@juicytraffic/contracts";
import { useRouter } from "./router";

interface SiteContextValue {
  sites: ProvisionedSite[];
  activeSite: ProvisionedSite;
  selectSite: (siteId: string) => void;
}

const SiteContext = createContext<SiteContextValue | null>(null);
const storageKey = "juicytraffic.active-site";

export function SiteProvider({ sites, children }: { sites: ProvisionedSite[]; children: ReactNode }) {
  const { path, search, navigate } = useRouter();
  const params = new URLSearchParams(search);
  const requestedId = params.get("site");
  const storedId = (() => { try { return localStorage.getItem(storageKey); } catch { return null; } })();
  const activeSite = sites.find((site) => site.site_id === requestedId)
    || sites.find((site) => site.site_id === storedId)
    || sites[0];

  useEffect(() => {
    if (!activeSite) return;
    try { localStorage.setItem(storageKey, activeSite.site_id); } catch { /* Storage is optional. */ }
    if (requestedId !== activeSite.site_id) {
      const next = new URLSearchParams(search);
      next.set("site", activeSite.site_id);
      navigate(`${path}?${next.toString()}`);
    }
  }, [activeSite, navigate, path, requestedId, search]);

  const value = useMemo<SiteContextValue>(() => ({
    sites,
    activeSite,
    selectSite: (siteId) => {
      const next = new URLSearchParams(search);
      next.set("site", siteId);
      navigate(`${path}?${next.toString()}`);
    },
  }), [activeSite, path, search, sites, navigate]);

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

export function useSite() {
  const context = useContext(SiteContext);
  if (!context) throw new Error("useSite must be used within SiteProvider");
  return context;
}
